/**
 * Repository 层 — 领域操作封装
 *
 * 组件不直接调 Storage，而是通过 Repository。
 * Repository 封装领域语义（如「添加生词」涉及 vocab + block 两处写入）。
 *
 * 数据流：组件 → Store → Repository → Storage
 */

import type { VocabWord, Library, LibraryDocument, Vocab, Annotation } from '@hump/shared-types';
import { getStorage } from '../storage/factory';
// #15 循环依赖清理：原为在 removeDocument 内动态 import('./knowledge')。
// 经核查 knowledge.ts 仅依赖 idb-keyval（动态）+ shared-types + ../dictionary，
// 不引用回 repository/index.ts，不存在循环依赖，故改为静态 import。
import { knowledgeRepository } from './knowledge';

// ============================================================================
// 生词本 Repository
// ============================================================================

export const vocabRepository = {
  /** 获取全部生词 */
  async getAll(): Promise<VocabWord[]> {
    const storage = getStorage();
    const vocab = await storage.readVocab();
    // 兜底：IndexedDB key 不存在时返回 undefined
    return vocab?.words ?? [];
  },

  /** 添加生词（去重 + 来源追加） */
  async addWord(word: VocabWord): Promise<void> {
    const storage = getStorage();
    const vocab = (await storage.readVocab()) ?? { version: '1.0', words: [] };
    const existing = vocab.words.find((w) => w.word === word.word);

    if (existing) {
      // 已存在：合并 sources 和 examples，不覆盖释义
      existing.sources.push(...word.sources);
      existing.examples.push(...word.examples);
    } else {
      vocab.words.push(word);
    }

    await storage.writeVocab(vocab);
  },

  /** 删除生词 */
  async removeWord(wordId: string): Promise<void> {
    const storage = getStorage();
    const vocab = (await storage.readVocab()) ?? { version: '1.0', words: [] };
    vocab.words = vocab.words.filter((w) => w.id !== wordId);
    await storage.writeVocab(vocab);
  },

  /** 从划词收藏生词（带来源上下文 + 离线词典释义补充） */
  async addWordFromSelection(
    word: string,
    docId: string,
    blockId: string,
    context: string
  ): Promise<void> {
    const storage = getStorage();
    const vocab = (await storage.readVocab()) ?? { version: '1.0', words: [] };
    const existing = vocab.words.find((w) => w.word.toLowerCase() === word.toLowerCase());

    const source = { docId, blockId, addedAt: new Date().toISOString() };
    const example = { text: context, docId, blockId };

    // 查离线词典补充释义（避免循环依赖，动态导入）
    let dictEntry: { phonetic?: string; pos?: string; definitions: string[] } | null = null;
    try {
      const { lookupWord } = await import('../dictionary');
      dictEntry = lookupWord(word);
    } catch {
      // 词典不可用，跳过
    }

    if (existing) {
      existing.sources.push(source);
      existing.examples.push(example);
      // 如果离线词典有新释义且词条还没有释义，补充
      if (dictEntry && existing.definitions.length === 0) {
        existing.definitions = dictEntry.definitions;
        if (!existing.phonetic && dictEntry.phonetic) existing.phonetic = dictEntry.phonetic;
        if (!existing.pos && dictEntry.pos) existing.pos = dictEntry.pos;
      }
    } else {
      vocab.words.push({
        id: 'word_' + crypto.randomUUID(),
        word,
        phonetic: dictEntry?.phonetic,
        pos: dictEntry?.pos,
        definitions: dictEntry?.definitions ?? [],
        examples: [example],
        sources: [source],
      });
    }
    await storage.writeVocab(vocab);
  },
};

// ============================================================================
// 批注 Repository
// ============================================================================

export const annotationRepository = {
  /** 给 block 加批注 */
  async addAnnotation(docId: string, blockId: string, text: string): Promise<void> {
    const storage = getStorage();
    const block = await storage.readBlock(docId, blockId);
    block.annotations.push({
      id: 'a_' + crypto.randomUUID(),
      text,
      createdAt: new Date().toISOString(),
    });
    block.updatedAt = new Date().toISOString();
    await storage.writeBlock(docId, block);
  },

  /** 更新批注文本（8.6 批注编辑） */
  async updateAnnotation(
    docId: string,
    blockId: string,
    annotationId: string,
    text: string
  ): Promise<void> {
    const storage = getStorage();
    const block = await storage.readBlock(docId, blockId);
    const annotation = block.annotations.find((a) => a.id === annotationId);
    if (!annotation) {
      throw new Error('批注不存在');
    }
    annotation.text = text;
    block.updatedAt = new Date().toISOString();
    await storage.writeBlock(docId, block);
  },

  /** 读取 block 的全部批注（8.6 编辑器展示） */
  async listAnnotations(docId: string, blockId: string): Promise<Annotation[]> {
    const storage = getStorage();
    const block = await storage.readBlock(docId, blockId);
    return block.annotations ?? [];
  },

  /** 删除批注 */
  async removeAnnotation(docId: string, blockId: string, annotationId: string): Promise<void> {
    const storage = getStorage();
    const block = await storage.readBlock(docId, blockId);
    block.annotations = block.annotations.filter((a) => a.id !== annotationId);
    block.updatedAt = new Date().toISOString();
    await storage.writeBlock(docId, block);
  },
};

// ============================================================================
// 资料库 Repository
// ============================================================================

export const libraryRepository = {
  /** 获取资料库索引 */
  async getLibrary(): Promise<Library> {
    const lib = await getStorage().readLibrary();
    // 兜底：IndexedDB key 不存在时返回 undefined
    if (!lib) {
      return {
        version: '1.0',
        rootDir: '',
        documents: [],
        routes: [],
        settings: { defaultLang: 'zh', theme: 'auto' },
      };
    }
    return lib;
  },

  /** 添加文档到资料库 */
  async addDocument(doc: LibraryDocument): Promise<void> {
    const storage = getStorage();
    const lib = await this.getLibrary();
    lib.documents.push(doc);
    await storage.writeLibrary(lib);
  },

  /** 删除文档（软删除 + 知识点孤儿清理） */
  async removeDocument(docId: string): Promise<void> {
    const storage = getStorage();
    const lib = await this.getLibrary();
    lib.documents = lib.documents.filter((d) => d.id !== docId);
    await storage.writeLibrary(lib);

    // 知识点孤儿清理：标记关联知识点的来源已失效
    // #15 已改为静态 import，无需 try/catch 包裹模块加载
    try {
      await knowledgeRepository.markDeletedByDoc(docId);
    } catch {
      // 标记失败不阻断删除主流程
    }
  },

  /** 更新文档阅读进度 */
  async updateProgress(docId: string, progress: number, lastReadBlock: string): Promise<void> {
    const storage = getStorage();
    const lib = await this.getLibrary();
    const doc = lib.documents.find((d) => d.id === docId);
    if (doc) {
      doc.progress = progress;
      doc.lastReadBlock = lastReadBlock;
      await storage.writeLibrary(lib);
    }
  },

  /** 初始化空 library + vocab（首次启动） */
  async ensureInitialized(): Promise<void> {
    const storage = getStorage();

    // readLibrary 在 IndexedDB key 不存在时返回 undefined（不抛异常）
    // 必须显式检查，不能依赖 catch
    let lib: Library | undefined;
    try {
      lib = await storage.readLibrary();
    } catch {
      lib = undefined;
    }
    if (!lib) {
      const emptyLib: Library = {
        version: '1.0',
        rootDir: '',
        documents: [],
        routes: [],
        settings: { defaultLang: 'zh', theme: 'auto' },
      };
      await storage.writeLibrary(emptyLib);
    }

    let vocab: Vocab | undefined;
    try {
      vocab = await storage.readVocab();
    } catch {
      vocab = undefined;
    }
    if (!vocab) {
      const emptyVocab: Vocab = {
        version: '1.0',
        words: [],
      };
      await storage.writeVocab(emptyVocab);
    }
  },
};
